import { useEffect, useRef, useState } from 'react';
import { shouldUseLocalLibraryIpc } from '../services/localLibraryIpcGate';
import { libraryService } from '../services/libraryService';
import {
  needsArtistPortrait,
  needsAlbumCover,
  resolveArtistPortrait,
  resolveAlbumCover,
  persistArtistPortrait,
  persistAlbumCover,
  markArtistEnrichmentFailed,
  markAlbumEnrichmentFailed,
  runEnrichmentPool,
  shouldRetryEnrichment,
} from '../services/enrichmentService';
import { ARTIST_CATALOG_FETCH_LIMIT } from '../constants/libraryCatalogLimits';
import { getArtistCatalogMapCached, peekArtistCatalogMapCached } from '../services/artistEnrichmentCatalogCache';
import { isMissingArtwork } from '../utils/formatDetection';
import { normalizeCoverHash, subscribeCoverCacheLoaded } from '../lib/coverArtIPC';
import { isAppIdle, subscribeAppIdle } from '../utils/appIdleMonitor';
import { finishGridEntryPhase, markGridEntryPhaseStart } from '../utils/gridEntryPerf';
import {
  deferToIdle,
  idleYield,
  installEnrichmentInteractionPause,
  waitForEnrichmentSlot,
} from '../utils/enrichmentScheduler';
import {
  isAlbumArtTerminal,
  isTerminalArtworkPhase,
  loadCoversDetailsRegistry,
  mergeArtworkState,
  reconcileArtworkBootstrapState,
  resolveCoversDetailsLibraryKey,
  saveCoversDetailsRegistry,
} from '../services/coversDetailsRegistry';

const ENRICHMENT_BATCH_SIZE = 48;
const ENRICHMENT_CONCURRENCY = 3;

/** Per-album cover patches — avoids setAlbums() on every enrichment hit (grid stays stable). */
const albumCoverOverlays = new Map();
const overlaySubscribers = new Map();

/** Aggregate artwork phase counter — pub/sub only, never touches album grid state. */
const ARTWORK_NOTIFY_MS = 500;
const ARTWORK_REGISTRY_PERSIST_MS = 750;
/** No per-tile Updating… badge may outlive a genuine in-flight task beyond this. */
export const ARTWORK_BADGE_MAX_MS = 120_000;
const artworkAlbumState = new Map();
/** Album ids with enrichment queued or actively resolving cover art. */
const enrichmentActiveAlbums = new Set();
let artworkPhaseStartedAt = 0;
let artworkBadgeFallbackTimer = null;
const albumUpdatingBadgeListeners = new Set();
let artworkHashToAlbums = new Map();
let artworkPhaseActive = false;
let artworkTotalHint = 0;
let artworkLibraryKey = '';
let deferredArtworkBegin = null;
const artworkProgressListeners = new Set();
let artworkNotifyTimer = null;
let artworkPendingNotify = false;
let artworkRegistryPersistTimer = null;
let unsubCoverCache = null;

/** @typedef {'filled' | 'exhausted' | 'pending'} ArtworkAlbumPhaseState */

function readMachineIdForRegistry() {
  try {
    return localStorage.getItem('maestro_machine_id') || '';
  } catch {
    return '';
  }
}

function scheduleArtworkRegistryPersist() {
  if (!artworkLibraryKey) return;
  if (artworkRegistryPersistTimer) return;
  artworkRegistryPersistTimer = setTimeout(() => {
    artworkRegistryPersistTimer = null;
    if (!artworkLibraryKey) return;
    saveCoversDetailsRegistry(artworkLibraryKey, artworkAlbumState);
  }, ARTWORK_REGISTRY_PERSIST_MS);
}

function persistArtworkRegistryNow() {
  if (artworkRegistryPersistTimer) {
    clearTimeout(artworkRegistryPersistTimer);
    artworkRegistryPersistTimer = null;
  }
  if (!artworkLibraryKey) return;
  saveCoversDetailsRegistry(artworkLibraryKey, artworkAlbumState);
}

function notifyAlbumUpdatingBadgeListeners() {
  for (const fn of albumUpdatingBadgeListeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function markAlbumEnrichmentActive(albumId) {
  if (!albumId) return;
  enrichmentActiveAlbums.add(String(albumId));
  notifyAlbumUpdatingBadgeListeners();
}

export function clearAlbumEnrichmentActive(albumId) {
  if (!albumId) return;
  const id = String(albumId);
  if (!enrichmentActiveAlbums.delete(id)) return;
  notifyAlbumUpdatingBadgeListeners();
}

export function isAlbumEnrichmentActive(albumId) {
  if (!albumId) return false;
  return enrichmentActiveAlbums.has(String(albumId));
}

/**
 * Per-tile Updating… badge — only while enrichment is genuinely queued/in-flight.
 * Provisional boot snapshot tiles are handled separately in AlbumGrid.
 */
export function shouldShowAlbumUpdatingBadge(album) {
  const id = String(album?.album_key || album?.id || '');
  if (!id) return false;
  if (!isAlbumEnrichmentActive(id)) return false;
  const phase = artworkAlbumState.get(id);
  return phase === 'pending';
}

export function subscribeAlbumUpdatingBadges(listener) {
  albumUpdatingBadgeListeners.add(listener);
  return () => { albumUpdatingBadgeListeners.delete(listener); };
}

function scheduleArtworkBadgeFallback() {
  if (artworkBadgeFallbackTimer) return;
  artworkBadgeFallbackTimer = setTimeout(() => {
    artworkBadgeFallbackTimer = null;
    applyArtworkBadgeFallback();
    if (artworkPhaseActive) scheduleArtworkBadgeFallback();
  }, Math.min(ARTWORK_BADGE_MAX_MS, 30_000));
}

function applyArtworkBadgeFallback() {
  if (!artworkPhaseActive) return false;
  const elapsed = Date.now() - artworkPhaseStartedAt;
  if (elapsed < ARTWORK_BADGE_MAX_MS) return false;
  let changed = false;
  for (const [id, state] of artworkAlbumState) {
    if (state !== 'pending') continue;
    if (enrichmentActiveAlbums.has(id)) continue;
    artworkAlbumState.set(id, 'exhausted');
    changed = true;
  }
  if (changed) {
    scheduleArtworkProgressNotify();
    persistArtworkRegistryNow();
    notifyAlbumUpdatingBadgeListeners();
  }
  return changed;
}

function artworkBootstrapState(album) {
  return reconcileArtworkBootstrapState(album, {
    enrichmentRetryOk: !album?.enrichment_attempted_at
      || shouldRetryEnrichment(album.enrichment_attempted_at),
  });
}

function getArtworkProgressSnapshot() {
  let withArtwork = 0;
  let exhausted = 0;
  let pending = 0;
  for (const state of artworkAlbumState.values()) {
    if (state === 'filled') withArtwork += 1;
    else if (state === 'exhausted') exhausted += 1;
    else pending += 1;
  }
  const total = Math.max(artworkAlbumState.size, artworkTotalHint);
  return {
    total,
    withArtwork,
    exhausted,
    pending,
    missing: pending,
  };
}

function scheduleArtworkProgressNotify() {
  if (!artworkPhaseActive) return;
  artworkPendingNotify = true;
  if (artworkNotifyTimer) return;
  artworkNotifyTimer = setTimeout(() => {
    artworkNotifyTimer = null;
    if (!artworkPendingNotify) return;
    artworkPendingNotify = false;
    const snap = getArtworkProgressSnapshot();
    for (const fn of artworkProgressListeners) {
      try { fn(snap); } catch { /* listener errors must not break progress */ }
    }
  }, ARTWORK_NOTIFY_MS);
}

export function getArtworkProgress() {
  return getArtworkProgressSnapshot();
}

export function subscribeArtworkProgress(listener) {
  artworkProgressListeners.add(listener);
  if (artworkPhaseActive) {
    try { listener(getArtworkProgressSnapshot()); } catch { /* ignore */ }
  }
  return () => artworkProgressListeners.delete(listener);
}

function markAlbumArtworkResolved(albumId) {
  if (!artworkPhaseActive || !albumId) return;
  const id = String(albumId);
  if (!artworkAlbumState.has(id)) return;
  if (artworkAlbumState.get(id) === 'filled') return;
  artworkAlbumState.set(id, 'filled');
  scheduleArtworkProgressNotify();
  scheduleArtworkRegistryPersist();
}

export function markAlbumArtworkExhausted(albumId) {
  if (!artworkPhaseActive || !albumId) return;
  const id = String(albumId);
  if (!artworkAlbumState.has(id)) return;
  if (artworkAlbumState.get(id) === 'filled') return;
  if (artworkAlbumState.get(id) === 'exhausted') return;
  artworkAlbumState.set(id, 'exhausted');
  scheduleArtworkProgressNotify();
  scheduleArtworkRegistryPersist();
}

/** Idle fallback — albums never attempted (e.g. enrichment pool cap) become terminal. */
export function markRemainingArtworkPendingExhausted() {
  if (!artworkPhaseActive) return false;
  let changed = false;
  for (const [id, state] of artworkAlbumState) {
    if (state === 'pending') {
      artworkAlbumState.set(id, 'exhausted');
      changed = true;
    }
  }
  if (changed) {
    scheduleArtworkProgressNotify();
    // Idle exhaustion used to live only in memory — reload after a bundle
    // deploy restarted 2307/2693 counting. Persist so deploy ≠ reset.
    persistArtworkRegistryNow();
  }
  return changed;
}

export function isArtworkPhaseActive() {
  return artworkPhaseActive;
}

function registerArtworkHashMappings(albums) {
  artworkHashToAlbums = new Map();
  for (const album of albums) {
    const albumId = String(album?.album_key || album?.id || '');
    if (!albumId) continue;
    for (const raw of [album.embedded_cover_hash, album.folder_cover_hash]) {
      const norm = normalizeCoverHash(raw);
      if (!norm) continue;
      if (!artworkHashToAlbums.has(norm)) artworkHashToAlbums.set(norm, new Set());
      artworkHashToAlbums.get(norm).add(albumId);
    }
  }
}

function onCoverHashLoaded(hash) {
  if (!artworkPhaseActive) return;
  const ids = artworkHashToAlbums.get(hash);
  if (!ids) return;
  for (const id of ids) markAlbumArtworkResolved(id);
}

function resetArtworkProgressInternal() {
  deferredArtworkBegin = null;
  if (artworkLibraryKey && artworkAlbumState.size > 0) {
    persistArtworkRegistryNow();
  }
  artworkPhaseActive = false;
  artworkAlbumState.clear();
  artworkHashToAlbums = new Map();
  artworkTotalHint = 0;
  artworkLibraryKey = '';
  enrichmentActiveAlbums.clear();
  artworkPhaseStartedAt = 0;
  if (artworkBadgeFallbackTimer) {
    clearTimeout(artworkBadgeFallbackTimer);
    artworkBadgeFallbackTimer = null;
  }
  notifyAlbumUpdatingBadgeListeners();
  if (unsubCoverCache) {
    unsubCoverCache();
    unsubCoverCache = null;
  }
  if (artworkNotifyTimer) {
    clearTimeout(artworkNotifyTimer);
    artworkNotifyTimer = null;
  }
  if (artworkRegistryPersistTimer) {
    clearTimeout(artworkRegistryPersistTimer);
    artworkRegistryPersistTimer = null;
  }
  artworkPendingNotify = false;
}

/** True when album is catalog-terminal or already settled in the registry. */
export function shouldFetchAlbumCoverEnrichment(album) {
  if (!album) return false;
  if (isAlbumArtTerminal(album)) return false;
  const id = String(album.album_key || album.id || '');
  if (id && isTerminalArtworkPhase(artworkAlbumState.get(id))) return false;
  return needsAlbumCover(album);
}

/**
 * Verify-only path for settled albums — no ArtGate / mirror network.
 * Returns true when a fetch was issued.
 */
export async function enrichAlbumCoverIfNeeded(album, fetchCover) {
  if (!shouldFetchAlbumCoverEnrichment(album)) return false;
  if (typeof fetchCover !== 'function') return false;
  await fetchCover(album);
  return true;
}

export function retryDeferredArtworkProgress() {
  if (!deferredArtworkBegin) return false;
  const { albums, totalHint } = deferredArtworkBegin;
  return beginArtworkProgress(albums, totalHint);
}

/** Bootstrap artwork phase counter from a one-shot album snapshot (not React state). */
export function beginArtworkProgress(albums, totalHint = 0) {
  const libraryKey = resolveCoversDetailsLibraryKey(readMachineIdForRegistry());
  if (!libraryKey) {
    deferredArtworkBegin = { albums, totalHint };
    console.info('[coversDetailsRegistry] defer beginArtworkProgress — machineId unavailable, no last-known key');
    return false;
  }
  deferredArtworkBegin = null;
  if (libraryKey.startsWith('machine:') && libraryKey !== 'machine:local') {
    try { localStorage.setItem('maestro_machine_id', libraryKey.slice('machine:'.length)); } catch { /* ignore */ }
  }
  resetArtworkProgressInternal();
  artworkPhaseActive = true;
  artworkPhaseStartedAt = Date.now();
  artworkTotalHint = totalHint > 0 ? totalHint : 0;
  artworkLibraryKey = libraryKey;
  // Hydrate terminal states from prior session — bundle deploys must not reset.
  const persisted = loadCoversDetailsRegistry(artworkLibraryKey);
  for (const album of albums || []) {
    const id = String(album?.album_key || album?.id || '');
    if (!id) continue;
    const live = artworkBootstrapState(album);
    artworkAlbumState.set(id, mergeArtworkState(live, persisted[id]));
  }
  // First getAlbums chunk is 200; persist the rest so relaunch does not
  // re-count settled albums that were not in this snapshot.
  for (const [id, state] of Object.entries(persisted)) {
    if (!id || artworkAlbumState.has(id)) continue;
    if (isTerminalArtworkPhase(state)) artworkAlbumState.set(id, state);
  }
  if (artworkTotalHint === 0 && artworkAlbumState.size > 0) {
    artworkTotalHint = artworkAlbumState.size;
  }
  const snap = getArtworkProgressSnapshot();
  console.info('[coversDetailsRegistry] hydrate', {
    libraryKey: artworkLibraryKey,
    persisted: Object.keys(persisted).length,
    filled: snap.withArtwork,
    exhausted: snap.exhausted,
    pending: snap.pending,
    total: snap.total,
  });
  registerArtworkHashMappings(albums);
  unsubCoverCache = subscribeCoverCacheLoaded(onCoverHashLoaded);
  scheduleArtworkProgressNotify();
  scheduleArtworkBadgeFallback();
  persistArtworkRegistryNow();
  notifyAlbumUpdatingBadgeListeners();
  return true;
}

export function resetArtworkProgress() {
  resetArtworkProgressInternal();
}

function notifyAlbumCoverOverlay(albumId) {
  const subs = overlaySubscribers.get(albumId);
  if (!subs) return;
  for (const fn of subs) fn();
}

export function applyAlbumCoverOverlay(albumId, patch) {
  if (!albumId || !patch) return;
  const prev = albumCoverOverlays.get(albumId) || {};
  albumCoverOverlays.set(albumId, { ...prev, ...patch });
  notifyAlbumCoverOverlay(albumId);
  if (patch.cover_art_url || patch.embedded_cover_hash) {
    markAlbumArtworkResolved(albumId);
  } else if (patch.enrichment_attempted_at) {
    markAlbumArtworkExhausted(albumId);
  }
}

export function clearAlbumCoverOverlays() {
  if (albumCoverOverlays.size === 0) return;
  albumCoverOverlays.clear();
}

export function useAlbumCoverOverlay(albumId) {
  const [, bump] = useState(0);
  useEffect(() => {
    if (!albumId) return undefined;
    const fn = () => bump((n) => n + 1);
    let set = overlaySubscribers.get(albumId);
    if (!set) {
      set = new Set();
      overlaySubscribers.set(albumId, set);
    }
    set.add(fn);
    return () => {
      set.delete(fn);
      if (set.size === 0) overlaySubscribers.delete(albumId);
    };
  }, [albumId]);
  return albumId ? albumCoverOverlays.get(albumId) || null : null;
}

function hasArtistPortraitUrl(entry) {
  const url = String(entry?.image_url || '').trim();
  return url.length > 0;
}

/** Merge getArtists snapshot with prior catalog — never let stale null portraits mask DB URLs. */
export function mergeArtistCatalogMaps(catalogMap, prev = {}) {
  const merged = { ...prev };
  for (const [name, fresh] of Object.entries(catalogMap || {})) {
    if (!name || !fresh) continue;
    const stale = prev[name];
    if (!stale) {
      merged[name] = fresh;
      continue;
    }
    const freshHasPortrait = hasArtistPortraitUrl(fresh);
    const staleHasPortrait = hasArtistPortraitUrl(stale);
    merged[name] = {
      ...stale,
      ...fresh,
      ...(freshHasPortrait
        ? {
          image_url: fresh.image_url,
          image_source: fresh.image_source ?? stale.image_source ?? null,
        }
        : staleHasPortrait
          ? {
            image_url: stale.image_url,
            image_source: stale.image_source ?? fresh.image_source ?? null,
          }
          : {
            image_url: fresh.image_url ?? stale.image_url ?? null,
            image_source: fresh.image_source ?? stale.image_source ?? null,
          }),
    };
  }
  return merged;
}

/**
 * Background image enrichment for local library artists and albums.
 * Grid renders immediately; portraits and covers fill in progressively.
 */
export function useLibraryImageEnrichment({
  displayAlbums,
  artists,
  activeCategory,
  searchQuery,
  catalogRevision,
  setArtistCatalog,
}) {
  const [artworkEnrichmentKick, setArtworkEnrichmentKick] = useState(0);
  const [appIdle, setAppIdle] = useState(() => isAppIdle());
  const artistsRef = useRef(artists);
  artistsRef.current = artists;
  const enrichmentQueueRevisionRef = useRef('');

  useEffect(() => subscribeAppIdle(setAppIdle), []);
  useEffect(() => installEnrichmentInteractionPause(), []);

  useEffect(() => {
    let kicked = false;
    return subscribeArtworkProgress((snap) => {
      if (!kicked && snap.total > 0 && snap.pending > 0) {
        kicked = true;
        setArtworkEnrichmentKick((n) => n + 1);
      }
    });
  }, []);

  useEffect(() => {
    if (!shouldUseLocalLibraryIpc()) return;
    let cancelled = false;

    const enrichArtist = async (artist) => {
      if (!artist?.id || !artist?.name) return;
      const result = await resolveArtistPortrait(artist.name);
      if (cancelled) return;
      if (result?.image_url) {
        await persistArtistPortrait(artist.id, result);
        if (cancelled) return;
        setArtistCatalog((prev) => ({
          ...prev,
          [artist.name]: {
            ...(prev[artist.name] || artist),
            id: artist.id,
            name: artist.name,
            image_url: result.image_url,
            image_source: result.source,
          },
        }));
      } else {
        await markArtistEnrichmentFailed(artist.id);
        setArtistCatalog((prev) => ({
          ...prev,
          [artist.name]: {
            ...(prev[artist.name] || artist),
            enrichment_attempted_at: new Date().toISOString(),
          },
        }));
      }
    };

    const enrichAlbum = async (album) => {
      const albumId = album?.album_key || album?.id;
      if (!albumId || !album?.title) return;
      if (!shouldFetchAlbumCoverEnrichment(album)) return;
      markAlbumEnrichmentActive(albumId);
      try {
        const result = await resolveAlbumCover(album.title, album.artist_name);
        if (cancelled) return;
        if (result?.cover_art_url) {
          const resp = await persistAlbumCover(albumId, result);
          if (cancelled) return;
          const coverHash = resp?.cover_hash || null;
          applyAlbumCoverOverlay(albumId, {
            cover_art_url: result.cover_art_url,
            ...(coverHash ? { embedded_cover_hash: coverHash } : {}),
          });
        } else {
          await markAlbumEnrichmentFailed(albumId);
          applyAlbumCoverOverlay(albumId, {
            enrichment_attempted_at: new Date().toISOString(),
          });
          markAlbumArtworkExhausted(albumId);
        }
      } finally {
        clearAlbumEnrichmentActive(albumId);
      }
    };

    const runEnrichment = async () => {
      if (appIdle || cancelled) return;
      const recordEnrichmentPhase = activeCategory === 'artists' || activeCategory === 'albums';
      const kickStart = recordEnrichmentPhase ? markGridEntryPhaseStart() : 0;
      try {
        const catalogRevisionKey = String(catalogRevision ?? '');
        const hadCachedCatalog = Boolean(peekArtistCatalogMapCached(catalogRevisionKey));
        const catalogMap = await getArtistCatalogMapCached(catalogRevisionKey, async () => {
          const artistResp = await libraryService.getArtists({ limit: ARTIST_CATALOG_FETCH_LIMIT });
          const map = {};
          for (const a of artistResp?.artists || artistResp?.items || []) {
            if (a?.name) map[a.name] = a;
          }
          return map;
        });
        if (cancelled) return;

        setArtistCatalog((prev) => mergeArtistCatalogMaps(catalogMap, prev));

        const q = String(searchQuery || '').trim().toLowerCase();
        const visibleArtists = artistsRef.current || [];
        const visibleArtistNames = new Set(
          visibleArtists
            .filter((a) => !a.image_url && (!q || a.name.toLowerCase().includes(q)))
            .map((a) => a.name),
        );
        const albumList = displayAlbums;
        const visibleAlbumIds = new Set(
          albumList
            .filter((a) => isMissingArtwork(a))
            .filter((a) => {
              if (activeCategory === 'albums' && q) {
                const title = String(a.title || '').toLowerCase();
                const artist = String(a.artist_name || '').toLowerCase();
                return title.includes(q) || artist.includes(q);
              }
              return activeCategory === 'albums' || activeCategory === 'recent';
            })
            .map((a) => a.album_key || a.id),
        );

        const queueRevisionKey = `${catalogRevisionKey}:${activeCategory}:${artworkEnrichmentKick}`;
        const queueAlreadyBuilt = enrichmentQueueRevisionRef.current === queueRevisionKey;
        if (queueAlreadyBuilt && hadCachedCatalog) {
          if (recordEnrichmentPhase && kickStart) {
            finishGridEntryPhase('enrichment_kickoff', kickStart);
          }
          return;
        }
        enrichmentQueueRevisionRef.current = queueRevisionKey;

        const artistTasks = (activeCategory === 'artists' || activeCategory === 'recent')
          ? Object.values(catalogMap)
            .filter((a) => needsArtistPortrait(a))
            .sort((a, b) => {
              const av = visibleArtistNames.has(a.name) ? 0 : 1;
              const bv = visibleArtistNames.has(b.name) ? 0 : 1;
              return av - bv;
            })
            .slice(0, ENRICHMENT_BATCH_SIZE)
          : [];

        const albumTasks = (activeCategory === 'albums' || activeCategory === 'recent')
          ? albumList
            .filter((a) => shouldFetchAlbumCoverEnrichment(a))
            .sort((a, b) => {
              const ak = a.album_key || a.id;
              const bk = b.album_key || b.id;
              const av = visibleAlbumIds.has(ak) ? 0 : 1;
              const bv = visibleAlbumIds.has(bk) ? 0 : 1;
              return av - bv;
            })
            .slice(0, ENRICHMENT_BATCH_SIZE)
          : [];

        const queue = [
          ...artistTasks.map((item) => ({
            kind: 'artist',
            item,
            priority: visibleArtistNames.has(item.name) ? 0 : 1,
          })),
          ...albumTasks.map((item) => ({
            kind: 'album',
            item,
            priority: visibleAlbumIds.has(item.album_key || item.id) ? 0 : 1,
          })),
        ].sort((a, b) => a.priority - b.priority);

        for (const task of queue) {
          if (task.kind === 'album') {
            const albumId = task.item?.album_key || task.item?.id;
            if (albumId) markAlbumEnrichmentActive(albumId);
          }
        }

        if (recordEnrichmentPhase && kickStart) {
          finishGridEntryPhase('enrichment_kickoff', kickStart);
        }

        for (let offset = 0; offset < queue.length && !cancelled; offset += ENRICHMENT_BATCH_SIZE) {
          if (!(await waitForEnrichmentSlot(() => cancelled))) break;
          const chunk = queue.slice(offset, offset + ENRICHMENT_BATCH_SIZE);
          await runEnrichmentPool(
            chunk,
            async (task) => {
              if (!(await waitForEnrichmentSlot(() => cancelled))) return;
              if (task.kind === 'artist') await enrichArtist(task.item);
              else await enrichAlbum(task.item);
            },
            ENRICHMENT_CONCURRENCY,
            () => cancelled,
          );
          if (offset + ENRICHMENT_BATCH_SIZE < queue.length) {
            await idleYield();
          }
        }
      } catch {
        /* enrichment is best-effort */
      }
    };

    const cancelDefer = deferToIdle(() => { void runEnrichment(); }, 8000);

    return () => {
      cancelled = true;
      cancelDefer();
    };
  }, [displayAlbums, catalogRevision, activeCategory, searchQuery, artworkEnrichmentKick, appIdle, setArtistCatalog]);
}
